import React, { useEffect, useState } from 'react';
import { cryptoPairLabel } from '../composables/fetchCrypto';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bell } from 'lucide-react';
import {
  findSmarterArticlesAll,
  getGuru,
  deleteArticle,
  queueArticleNotification,
} from '../composables/fetchAPI';
import { fetchUsStockSymbols, fetchPolygonSnapshotPrice } from '../composables/fetchPolygon';
import SearchBar from '../components/SearchBar';
import Select from 'react-select';

const NOTIFY_CHANNEL_OPTIONS = ['IN_APP', 'WEB_PUSH', 'TELEGRAM'] as const;
type NotifyChannel = typeof NOTIFY_CHANNEL_OPTIONS[number];

const getContentPreview = (html: string, length = 100) => {
  const temp = document.createElement('div');
  temp.innerHTML = html || '';
  const text = temp.textContent || temp.innerText || '';
  return text.length > length ? text.slice(0, length) + '...' : text;
};
export const getGuruTextColor = (guruName?: string) => {
  switch (guruName) {
    case 'จารย์ Shay': return '#eab308'; // เหลือง
    case 'แอ๊ดอั้ม': return '#22c55e'; // เขียว
    case 'Alpha Pick': return '#f97316'; // ส้ม
    case 'Insiders วงใน': return '#3b82f6'; // น้ำเงิน
    case 'Weekly Report': return '#14b8a6'; // เขียวอมฟ้า (Teal)
    default: return '#ffffff'; // ขาว
  }
};
export const getGuruThemeStyle = (guruName?: string) => {
  switch (guruName) {
    case 'Alpha Pick':
      // สีส้ม
      return {
        backgroundColor: 'var(--theme-orange-bg, rgba(249, 115, 22, 0.1))',
        borderColor: 'var(--theme-orange-border, rgba(249, 115, 22, 0.4))',
        boxShadow: '0 8px 24px var(--theme-orange-shadow, rgba(249, 115, 22, 0.15))'
      };
    case 'จารย์ Shay':
      // สีเหลือง
      return {
        backgroundColor: 'var(--theme-yellow-bg, rgba(234, 179, 8, 0.1))',
        borderColor: 'var(--theme-yellow-border, rgba(234, 179, 8, 0.4))',
        boxShadow: '0 8px 24px var(--theme-yellow-shadow, rgba(234, 179, 8, 0.15))'
      };
    case 'แอ๊ดอั้ม':
      // สีเขียว
      return {
        backgroundColor: 'var(--theme-green-bg, rgba(34, 197, 94, 0.1))',
        borderColor: 'var(--theme-green-border, rgba(34, 197, 94, 0.4))',
        boxShadow: '0 8px 24px var(--theme-green-shadow, rgba(34, 197, 94, 0.15))'
      };
    case 'Insiders วงใน':
      // สีน้ำเงิน
      return {
        backgroundColor: 'var(--theme-blue-bg, rgba(59, 130, 246, 0.1))',
        borderColor: 'var(--theme-blue-border, rgba(59, 130, 246, 0.4))',
        boxShadow: '0 8px 24px var(--theme-blue-shadow, rgba(59, 130, 246, 0.15))'
      };
    case 'Weekly Report':
      // สีเขียวอมฟ้า (Teal)
      return {
        backgroundColor: 'var(--theme-teal-bg, rgba(13, 148, 136, 0.1))',
        borderColor: 'var(--theme-teal-border, rgba(13, 148, 136, 0.4))',
        boxShadow: '0 8px 24px var(--theme-teal-shadow, rgba(13, 148, 136, 0.15))'
      };
    default:
      // กรณีไม่มี Guru หรือไม่ตรงกับเงื่อนไข
      return {
        backgroundColor: 'var(--component-bg-color, #1e1e1e)',
        borderColor: 'var(--border-item-light, #333)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
      };
  }
};  

// Dummy ArticleItem component
const ArticleItem = ({
  article,
  guruList,
  onClick,
  onTagClick,
  onDelete,
  isAdmin,
}: {
  article: any;
  guruList: any[];
  onClick: () => void;
  onTagClick?: (ticker: string) => void;
  onDelete?: () => void;
  isAdmin?: boolean;
}) => {
  const isNew = new Date().getTime() - new Date(article.createdAt).getTime() <= 24 * 60 * 60 * 1000;

  // ช่องทางที่เคยกดส่งแล้ว (มี xxxAnnounceAt) — ล็อกไม่ให้ติ๊กซ้ำ ส่วนถ้าครบทั้ง 3 ช่องทางแล้วซ่อนปุ่มไปเลย
  const [sentChannels, setSentChannels] = useState<Record<NotifyChannel, boolean>>({
    IN_APP: !!article.inAppAnnounceAt,
    WEB_PUSH: !!article.webPushAnnounceAt,
    TELEGRAM: !!article.telegramAnnounceAt,
  });
  const allChannelsSent = NOTIFY_CHANNEL_OPTIONS.every((c) => sentChannels[c]);

  const [notifyOpen, setNotifyOpen] = useState(false);
  // เริ่มต้นติ๊กเฉพาะช่องทางที่ยังไม่เคยส่ง ส่วนที่ส่งไปแล้วปล่อยว่างไว้กันส่งซ้ำโดยไม่ตั้งใจ
  const [notifyChannels, setNotifyChannels] = useState<Record<NotifyChannel, boolean>>({
    IN_APP: !article.inAppAnnounceAt,
    WEB_PUSH: !article.webPushAnnounceAt,
    TELEGRAM: !article.telegramAnnounceAt,
  });
  const [sendingNotify, setSendingNotify] = useState(false);

  const handleSendNotification = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const channels = NOTIFY_CHANNEL_OPTIONS.filter((c) => notifyChannels[c] && !sentChannels[c]);
    if (channels.length === 0) {
      toast.error('เลือกอย่างน้อย 1 ช่องทาง');
      return;
    }
    setSendingNotify(true);
    try {
      const res = await queueArticleNotification(article.id, channels as NotifyChannel[]);
      toast.success(`ส่งแจ้งเตือนบทความแล้ว (${res.status})`);
      // อัปเดตสถานะในเครื่องทันทีไม่ต้องรีโหลดหน้า — ล็อกช่องที่เพิ่งส่งไป และถ้าครบ 3 ช่องทางแล้วปุ่มจะหายไปเอง
      setSentChannels((prev) => {
        const next = { ...prev };
        channels.forEach((c) => { next[c] = true; });
        return next;
      });
      setNotifyChannels((prev) => {
        const next = { ...prev };
        channels.forEach((c) => { next[c] = false; });
        return next;
      });
      setNotifyOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'ส่งแจ้งเตือนไม่สำเร็จ');
    } finally {
      setSendingNotify(false);
    }
  };

  // 1. หา Guru จากรายการ
  const guru = guruList.find((g: any) => g.id === article.guruId);
  const isShay = guru?.id === '5f8adbab-f7ed-4564-9c18-43e81f74c244';
const cardStyle = getGuruThemeStyle(guru?.name);
  // 2. จัดการสี Tag ของ Guru
  // Default: ไม่มีสี (พื้นใส ขอบเทา)
  let tagColor = 'bg-transparent text-gray-400 border border-gray-500';
  if (isShay) {
    tagColor = 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-transparent';
  } else if (guru?.name === 'แอ๊ดอั้ม') {
    tagColor = 'bg-green-600 text-white border-transparent';
  }

  // 3. เอฟเฟกต์สำหรับปกที่ไม่มีรูป (Bubble / ฟองสบู่)
  // ปรับให้ใช้ w-full h-full เพื่อให้ยืดเต็มกรอบที่เราฟิกซ์ไว้
  const BubbleCover = () => (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-500 shadow-[inset_0_10px_20px_rgba(255,255,255,0.6),inset_0_-10px_20px_rgba(0,0,0,0.2)] relative overflow-hidden">
      <div className="absolute top-2 left-4 w-12 h-4 bg-white/50 rounded-full blur-[2px] transform -rotate-12"></div>
      <div className="absolute bottom-4 right-4 w-8 h-8 bg-white/20 rounded-full blur-[4px]"></div>
      <span className="text-3xl font-black text-white tracking-widest drop-shadow-lg z-10 px-4 text-center truncate w-full">
        {article.tickers?.[0] || 'NEWS'}
      </span>
    </div>
  );

  return (
    <a
      href={`/article/${article.id}`}
      className={`relative group block rounded-xl mb-4 transition-all duration-300 shadow-lg hover:bg-gray-700/50 cursor-pointer p-[1px] bg-transparent`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.button === 1) return;
        e.preventDefault();
        if ((e.target as HTMLElement).closest('[data-no-nav]')) return;
        onClick();
      }}
    >
    <div 
        className="flex flex-col md:flex-row gap-4 h-full rounded-xl p-4 w-full border-[1.5px]"
        style={{
          ...cardStyle, // กระจาย property พื้นหลัง, กรอบ, เงา เข้าไปเลย
          color: 'var(--text-color, #fff)'
        }}
      >

        {isNew && (
          <div className="absolute -top-1 -right-1 md:top-0 md:right-0 bg-green-500 text-white text-[10px] md:text-sm font-bold px-2 py-1 md:px-4 md:py-2 rounded-full animate-blink z-20">
            NEW
          </div>
        )}

        {isAdmin && onDelete && (
          <button
            data-no-nav
            className="absolute top-2 right-2 md:top-3 md:right-3 w-8 h-8 md:opacity-0 md:group-hover:opacity-100 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-700 text-white text-xl font-bold z-30 transition-opacity"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              if (window.confirm('ต้องการจะลบบทความนี้ใช่หรือไม่?')) onDelete();
            }}
            title="ลบบทความ"
          >
            ×
          </button>
        )}

        {/* 1. Cover Section (ฟิกซ์ขนาดความสูง-กว้างให้เป๊ะ) */}
        <div className="w-full md:w-56 h-48 md:h-40 lg:h-48 flex-shrink-0 relative rounded-xl overflow-hidden shadow-md">
          {article.cover ? (
            <img src={article.cover} alt={article.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
          ) : (
            <BubbleCover />
          )}
        </div>

        {/* 2. Content Section (ใช้ justify-between ให้เนื้อหาถูกดันไปบน-ล่างพอดีกัน) */}
        <div className="flex flex-col flex-grow justify-between relative min-h-[12rem] min-w-0">
          <div>
            {/* ปรับให้เป็น flex-col เพื่อให้หัวข้ออยู่บรรทัดบน และ Tag อยู่ด้านล่างเสมอ ป้องกันการเบียดกัน */}
            <div className="flex flex-col gap-2 mb-2 pr-6 md:pr-8 w-full">
              
              {/* ลบ truncate ออก ใช้แค่ line-clamp และเพิ่ม break-words */}
              <h2 className="text-xl md:text-2xl font-bold m-0 leading-snug line-clamp-2 md:line-clamp-1 break-words w-full" style={{ color: 'var(--text-color)' }}>
                {article.title}
              </h2>
              
              <div className="flex flex-wrap items-center gap-2">
                {Array.isArray(article.tags) && article.tags.length > 0 && (
                  <span className="text-xs md:text-sm font-medium opacity-60" style={{ color: 'var(--text-color)' }}>
                    {article.tags.join(' / ')}
                  </span>
                )}
                
                {/* Guru Tag */}
                {guru && (
                  <span className={`animate-pulse text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full shadow-sm ${tagColor}`}>
                    {guru.name}
                  </span>
                )}
              </div>
            </div>

            {/* Preview Text */}
            <div className="text-sm md:text-base leading-relaxed mb-3 opacity-80 line-clamp-2 md:line-clamp-3 break-words" style={{ color: 'var(--text-color)' }}>
              {getContentPreview(article.content, 120)}
            </div>
          </div>

          {/* Bottom Row (คงเดิม) */}
          <div className="flex flex-row justify-between items-end gap-2 mt-2 flex-wrap">
            <div className="flex gap-2 flex-wrap items-center">
              {Array.isArray(article.tickers) && article.tickers.map((ticker: string, idx: number) => (
                <button
                  key={idx}
                  type="button"
                  className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs md:text-sm font-bold border border-yellow-500 hover:bg-yellow-300 transition-all active:scale-95"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onTagClick && onTagClick(ticker);
                  }}
                >
                  {cryptoPairLabel(ticker)}
                </button>
              ))}

              {isAdmin && !allChannelsSent && (
                <div
                  data-no-nav
                  className="relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-bold shadow-md transition-colors active:scale-95"
                    onClick={(e) => {
                      e.preventDefault();
                      setNotifyOpen((v) => !v);
                    }}
                    title="ส่งแจ้งเตือนบทความนี้"
                  >
                    <Bell size={14} /> ส่งแจ้งเตือน
                  </button>

                  {notifyOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNotifyOpen(false);
                        }}
                      />
                      <div
                        className="absolute left-0 bottom-full mb-2 w-56 rounded-xl shadow-2xl p-3 text-sm z-50"
                        style={{ backgroundColor: 'var(--component-bg-color, #18181b)', color: 'var(--text-color, #fff)', border: '1px solid var(--bg-item, #333)' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="font-bold mb-2 text-xs opacity-80">ส่งแจ้งเตือนบทความนี้</div>
                        <div className="flex flex-col gap-1.5 mb-3">
                          {NOTIFY_CHANNEL_OPTIONS.map((ch) => {
                            const alreadySent = sentChannels[ch];
                            return (
                              <label
                                key={ch}
                                className="flex items-center gap-2 text-xs cursor-pointer"
                                style={alreadySent ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                              >
                                <input
                                  type="checkbox"
                                  disabled={alreadySent}
                                  checked={notifyChannels[ch]}
                                  onChange={(e) => setNotifyChannels((prev) => ({ ...prev, [ch]: e.target.checked }))}
                                />
                                {ch}
                                {alreadySent && <span className="opacity-70">(ส่งแล้ว)</span>}
                              </label>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={sendingNotify}
                            onClick={handleSendNotification}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ backgroundColor: 'var(--button-create-bg, #22c55e)', color: 'var(--button-text-color, #000)' }}
                          >
                            {sendingNotify ? 'กำลังส่ง...' : 'ส่งแจ้งเตือน'}
                          </button>
                          <button
                            type="button"
                            disabled={sendingNotify}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNotifyOpen(false); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                            style={{ backgroundColor: 'var(--bg-item, #333)' }}
                          >
                            ยกเลิก
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>


              <div className="text-[10px] md:text-xs font-medium italic opacity-50 whitespace-nowrap" style={{ color: 'var(--text-color)' }}>
                ยอดคนดู: {Math.ceil(article.viewCount / 2)} Views
              </div>

          </div>
        </div>
      </div>
    </a>
  );
};

const SelectedTagList = ({
  guru,
  port,
  tickers,
  onRemoveGuru,
  onRemovePort,
  onRemoveTicker,
}: {
  guru?: { id: string; name: string };
  port?: { id: string; name: string };
  tickers: string[];
  onRemoveGuru: () => void;
  onRemovePort: () => void;
  onRemoveTicker: (ticker: string) => void;
}) => {
  if (!guru && !port && tickers.length === 0) return null;
  return (
    <div className="flex gap-2 mb-4 flex-wrap">
      {guru && (
        <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold border border-yellow-300 flex items-center">
          {guru.name}
          <button
            className="ml-2 text-yellow-800 hover:text-red-500 font-bold"
            onClick={onRemoveGuru}
            title="Remove Guru"
          >
            ×
          </button>
        </span>
      )}
      {port && (
        <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold border border-yellow-300 flex items-center">
          {port.name}
          <button
            className="ml-2 text-yellow-800 hover:text-red-500 font-bold"
            onClick={onRemovePort}
            title="Remove Guru Port"
          >
            ×
          </button>
        </span>
      )}
      {tickers.map((ticker) => (
        <span key={ticker} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold border border-yellow-300 flex items-center">
          {cryptoPairLabel(ticker)}
          <button
            className="ml-2 text-yellow-800 hover:text-red-500 font-bold"
            onClick={() => onRemoveTicker(ticker)}
            title="Remove Ticker"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};

const POPULAR_TICKERS = [
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'META', 'BRK.B', 'JPM', 'V', 'UNH', 'HD', 'PG', 'MA', 'XOM', 'O'
];

const ArticlePage: React.FC<{ userInfo?: any }> = ({ userInfo }) => {
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [guruList, setGuruList] = useState<any[]>([]);
  const [guruId, setGuruId] = useState('');
  const [portfield, setPortfield] = useState('');
  const [title, setTitle] = useState('');
  const [tickers, setTickers] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc'); // desc = New to Old

  // Portfolios for selected guru
  const [portfolioOptions, setPortfolioOptions] = useState<any[]>([]);

  const [tickerQuery, setTickerQuery] = useState('');
  const [tickerDropdown, setTickerDropdown] = useState(false);
  const [tickerResults, setTickerResults] = useState<{ symbol: string, name: string, price?: number }[]>([]);
  const [tickerLoading, setTickerLoading] = useState(false);
  const [popularTickerPrices, setPopularTickerPrices] = useState<{ symbol: string, price: number }[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // 2. Add isMobile Logic
  const [isMobile, setIsMobile] = useState(false);
  // State สำหรับเปิด-ปิด Filter บน Mobile
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 768);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);
  const navigate = useNavigate();
  // Get userInfo from props or context (adjust as needed)
  // Example: const { userInfo } = useContext(UserContext);

  useEffect(() => {
    // Fetch guru list for filter dropdown
    getGuru().then((data) => setGuruList(data));
  }, []);

  useEffect(() => {
    // Update portfolio options when guru changes
    if (guruId) {
      const selectedGuru = guruList.find((g: any) => g.id === guruId);
      setPortfolioOptions(selectedGuru?.portfolios || []);
      setPortfield(''); // Reset portfield when guru changes
    } else {
      setPortfolioOptions([]);
      setPortfield('');
    }
  }, [guruId, guruList]);

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line
  }, [guruId, portfield, title, tickers, page, sortOrder]);

  useEffect(() => {
    setPage(1);
    fetchArticles();
    // eslint-disable-next-line
  }, [portfield]);

  // Fetch prices for popular tickers (once)
  useEffect(() => {
    const fetchPopularPrices = async () => {
      const promises = POPULAR_TICKERS.map(async (symbol) => {
        const price = await fetchPolygonSnapshotPrice(symbol);
        return { symbol, price: price ?? 0 };
      });
      setPopularTickerPrices(await Promise.all(promises));
    };
    fetchPopularPrices();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await findSmarterArticlesAll({
        guruId,
        portfield,
        title,
        ticker: tickers.join(','),
        page: String(page),
        limit: '10',
      });
      let filteredArticles = res.items || [];
      // Sort locally if API doesn't support
      filteredArticles = filteredArticles.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });
      if (portfield) {
        filteredArticles = filteredArticles.filter(
          (article: any) => article.portfolioId === portfield
        );
      }
      setArticles(filteredArticles);
      setTotalPages(res.meta?.pages || 1);
    } catch (err) {
      setArticles([]);
    }
    setLoading(false);
  };

  const handleArticleClick = (id: string) => {
    navigate(`/article/${id}`);
  };

  // Handle ticker input change
  const handleTickerInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().trim();
    setTickerQuery(value);
    if (!value) {
      setTickerResults([]);
      setTickerDropdown(true);
      return;
    }
    setTickerLoading(true);
    const res = await fetchUsStockSymbols(value);
    // Optionally fetch prices for search results
    const withPrices = await Promise.all(res.map(async (item) => {
      const price = await fetchPolygonSnapshotPrice(item.symbol);
      return { ...item, price };
    }));
    setTickerResults(withPrices);
    setTickerDropdown(true);
    setTickerLoading(false);
  };

  // Handle ticker select from dropdown
  const handleTickerSelect = (symbol: string) => {
    setTickers([symbol]);
    setTickerQuery('');
    setTickerDropdown(false);
  };

  // Find selected guru and port objects for tag display
  const selectedGuru = guruList.find((g: any) => g.id === guruId);
  const selectedPort = portfolioOptions.find((p: any) => p.id === portfield);

  // Handlers for removing tags
  const handleRemoveGuru = () => setGuruId('');
  const handleRemovePort = () => setPortfield('');
  const handleRemoveTicker = (ticker: string) => setTickers(tickers.filter(t => t !== ticker));

  // Ticker select handler for multi-select
  const handleTickerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value).filter(Boolean);
    setTickers(selected);
  };

  const handleTickerTagClick = (ticker: string) => {
    setTickers([ticker]);
    setTickerQuery('');
    setPage(1);
  };

  // ลบ article
  const handleDeleteArticle = async (id: string) => {
    if (!window.confirm('ต้องการจะลบบทความนี้ใช่หรือไม่?')) return;
    setDeletingId(id);
    try {
      await deleteArticle(id);
      setArticles(articles.filter(a => a.id !== id));
    } catch (err) {
      alert('ลบไม่สำเร็จ');
    }
    setDeletingId(null);
  };

  return (
    <div className={`py-6 md:py-8 ${isMobile ? 'px-4' : 'px-24'}`}>
      <h1 className="text-4xl font-bold mb-4">บทความน่าสนใจ</h1>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <select
          value={guruId}
          onChange={(e) => setGuruId(e.target.value)}
          className="px-2 py-1 rounded bg-[#23232b] text-white border border-[#333] w-40"
        >
          <option value="">All Gurus</option>
         {guruList.map((g: any) => (
    <option 
      key={g.id} 
      value={g.id}
      style={{ 
        color: getGuruTextColor(g.name),
        fontWeight: 'bold' 
      }}
    >
      {g.name}
    </option>
  ))}
        </select>
        {/* <select
          value={portfield}
          onChange={(e) => setPortfield(e.target.value)}
          className="px-2 py-1 rounded bg-[#23232b] text-white border border-[#333] w-40"
          disabled={!guruId}
        >
          <option value="">All Guru Ports</option>
          {portfolioOptions.map((p: any) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select> */}

        {/* --- Custom Ticker Search --- */}
        <div className="relative w-64">
          <input
            className="w-full px-4 py-2 border text-white bg-[#23232b] border-[#333] rounded focus:outline-none focus:ring-2 focus:ring-yellow-400"
            type="text"
            placeholder="ค้นหาหุ้นหรือเลือก Ticker"
            value={tickerQuery}
            onChange={handleTickerInput}
            onFocus={() => setTickerDropdown(true)}
            onBlur={() => setTimeout(() => setTickerDropdown(false), 150)}
            style={{ fontWeight: 500, fontSize: '1rem' }}
          />
          {tickerDropdown && (
            <ul className="absolute left-0 right-0 bg-[#18181b] border border-[#333] rounded-lg shadow-lg mt-1 z-10 max-h-72 overflow-y-auto min-w-full">
              {!tickerQuery && (
                <>
                  <li className="px-4 py-2 text-xs text-yellow-400 font-bold sticky top-0 bg-[#23232b] border-b border-[#333]">
                    ★ Popular Tickers
                  </li>
                  {popularTickerPrices.map(item => (
                    <li
                      key={item.symbol}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-yellow-100/80 cursor-pointer bg-yellow-50 font-bold text-yellow-900 border-b border-yellow-100 transition"
                      onMouseDown={() => handleTickerSelect(item.symbol)}
                      style={{ fontSize: '1.05rem' }}
                    >
                      <span className="text-yellow-400 text-lg">★</span>
                      <span className="font-extrabold text-base w-20">{item.symbol}</span>
                      <span className="text-gray-500 flex-1 truncate">{item.price ? `$${item.price.toFixed(2)}` : ''}</span>
                      {tickers.includes(item.symbol) && (
                        <span className="text-green-600 font-bold ml-2">✔</span>
                      )}
                    </li>
                  ))}
                </>
              )}
              {tickerQuery && tickerLoading && (
                <li className="px-4 py-2 text-xs text-gray-400">Loading...</li>
              )}
              {tickerQuery && !tickerLoading && tickerResults.length === 0 && (
                <li className="px-4 py-2 text-xs text-gray-400">No results found</li>
              )}
              {tickerQuery && !tickerLoading && tickerResults.map(item => (
                <li
                  key={item.symbol}
                  className={`flex items-center gap-3 px-4 py-2 hover:bg-yellow-100/80 cursor-pointer border-b border-[#23232b] transition
                    ${POPULAR_TICKERS.includes(item.symbol)
                      ? 'bg-yellow-50 font-bold text-yellow-900'
                      : 'text-white bg-[#23232b]'}`}
                  onMouseDown={() => handleTickerSelect(item.symbol)}
                  style={{ fontSize: '1.05rem' }}
                >
                  {POPULAR_TICKERS.includes(item.symbol) && (
                    <span className="text-yellow-400 text-lg">★</span>
                  )}
                  <span className="font-extrabold text-base w-20">{item.symbol}</span>
                  <span className="text-gray-400 flex-1 truncate">{item.name}</span>
                  {/* <span className="text-gray-500">{item.price !== undefined ? `$${item.price?.toFixed(2)}` : ''}</span> */}
                  {tickers.includes(item.symbol) && (
                    <span className="text-green-600 font-bold ml-2">✔</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* --- End Custom Ticker Search --- */}

        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="px-2 py-1 bg-[#23232b] text-white border border-[#333] rounded w-40"
        />

        {/* <button
          className="bg-blue-500 text-white px-4 py-1 rounded"
          onClick={() => { setPage(1); fetchArticles(); }}
        >
          Filter
        </button> */}
        {userInfo?.isAdmin && (
          <button
            className="bg-yellow-400 text-gray-900 font-bold px-4 py-1 rounded-xl ml-2"
            onClick={() => navigate('/articles/create')}
          >
            Create Article
          </button>
        )}
      </div>
      {/* Sort Button */}
      <button
        className="mb-4 px-4 py-1  rounded "
        style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
      >
        เรียงจาก: {sortOrder === 'desc' ? 'ใหม่ → เก่า ▲' : 'เก่า → ใหม่ ▼'}
      </button>
      {/* Tag List */}
      <SelectedTagList
        guru={selectedGuru}
        port={selectedPort}
        tickers={tickers}
        onRemoveGuru={handleRemoveGuru}
        onRemovePort={handleRemovePort}
        onRemoveTicker={handleRemoveTicker}
      />

      {/* Article List */}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div>
          {articles.map((article) => (
            <ArticleItem
              key={article.id}
              article={article}
              guruList={guruList}
              onClick={() => handleArticleClick(article.id)}
              onTagClick={handleTickerTagClick}
              onDelete={userInfo?.isAdmin ? () => handleDeleteArticle(article.id) : undefined}
              isAdmin={userInfo?.isAdmin}
            />
          ))}
        </div>
      )}
      {/* Pagination */}
      <div className="flex justify-center mt-6 gap-2">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="px-3 py-1 border rounded"
        >
          Prev
        </button>
        <span className="px-3 py-1">{page} / {totalPages}</span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
          className="px-3 py-1 border rounded"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ArticlePage;